import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useWalkthrough } from '@/hooks/useWalkthrough';
import { useAuthContext } from '@/contexts/AuthContext';
import { homeDetailsService, formatCurrency, type ActivityStatus, type SalesLast30DaysResponse, type SalesByMonthResponse } from '@/services/homeDetailsService';
import { diamondsService } from '@/services/diamonds.service';
import DiamondDashboard from '@/components/DiamondDashboard';
import IndicacoesDashboard from '@/components/IndicacoesDashboard';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Diamond, 
  Plus, 
  FileText, 
  Trophy, 
  DollarSign,
  Users,
  Briefcase,
  HelpCircle,
  ShoppingBag,
  BookOpen,
  TrendingUp,
  Gift,
  Share2,
  ActivityIcon,
  Gem,
  ChartArea,
  LayoutDashboard,
  ArrowUp,
  ArrowDown,
  Medal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface StatCard {
  title: string;
  value: string | number;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon: React.ElementType;
  color: string;
}

const Index = () => {
  const navigate = useNavigate();
  const { checkFirstVisit, startWalkthrough } = useWalkthrough();
  const { user } = useAuthContext();
  const [TotalCoord, setTotalCoord] = useState<number>(0);
  const [activityStatus, setActivityStatus] = useState<ActivityStatus | null>(null);
  const [salesLast30Days, setSalesLast30Days] = useState<SalesLast30DaysResponse | null>(null);
  const [salesByMonth, setSalesByMonth] = useState<SalesByMonthResponse | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [diamondRanking, setDiamondRanking] = useState<any[]>([]);
  const [diamondSearchTerm, setDiamondSearchTerm] = useState<string>('');
  const [diamondSelectedRoles, setDiamondSelectedRoles] = useState<string[]>(['revendedora', 'cliente', 'admin', 'coordenadora']);
  const [diamondFiltersExpanded, setDiamondFiltersExpanded] = useState<boolean>(false);
  const [stats, setStats] = useState<StatCard[]>([
    {
      title: 'Revendedoras(es)',
      value: '--',
      icon: Users,
      color: 'text-primary'
    },
    {
      title: 'Clientes',
      value: '--',
      icon: ShoppingBag,
      color: 'text-warning'
    },
    {
      title: 'Total de Vendas',
      value: 'R$ 0,00',
      icon: DollarSign,
      color: 'text-success'
    },
    {
      title: 'Vendas Baixadas',
      value: 'R$ 0,00',
      icon: FileText,
      color: 'text-error'
    }
  ]);

  useEffect(() => {
    checkFirstVisit();
    loadHomeDetails();
    loadActivityStatus();
    loadSalesData();
    loadDiamondRanking();
  }, []);

  const loadSalesData = async () => {
    try {
      const [last30Days, byMonth] = await Promise.all([
        homeDetailsService.getSalesLast30Days(),
        homeDetailsService.getSalesByMonth()
      ]);
      setSalesLast30Days(last30Days);
      setSalesByMonth(byMonth);
      // Define o mês selecionado como o mês atual retornado pela API
      if (byMonth) {
        setSelectedMonth(`${byMonth.year}-${String(byMonth.month).padStart(2, '0')}`);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de vendas:', error);
    }
  };

  // Carrega dados quando o mês selecionado é alterado
  const loadSalesByMonth = async (monthYear: string) => {
    if (!monthYear) return;
    
    try {
      const [year, month] = monthYear.split('-');
      const data = await homeDetailsService.getSalesByMonth(
        parseInt(month),
        parseInt(year)
      );
      setSalesByMonth(data);
    } catch (error) {
      console.error('Erro ao carregar dados do mês selecionado:', error);
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      loadSalesByMonth(selectedMonth);
    }
  }, [selectedMonth]);

  const loadHomeDetails = async () => {
    const details = await homeDetailsService.findAll();
    
    if (details) {
      setTotalCoord(details.qtdCoordenadoras);
      setStats([
        {
          title: 'Revendedoras(es)',
          value: details.qtdRevendedora,
          icon: Users,
          color: 'text-primary'
        },
        {
          title: 'Clientes',
          value: details.qtdClientes,
          icon: ShoppingBag,
          color: 'text-warning'
        },
        {
          title: 'Total de Vendas',
          value: formatCurrency(details.totalAmountSales),
          icon: DollarSign,
          color: 'text-success'
        },
        {
          title: 'Vendas Baixadas',
          value: formatCurrency(details.totalAmountSalesSituations),
          icon: FileText,
          color: 'text-error'
        }
      ]);
    }
  };

  const loadActivityStatus = async () => {
    try {
      const activity = await homeDetailsService.getActivityStatus();
      setActivityStatus(activity);
    } catch (error) {
      console.error('Erro ao carregar activity status:', error);
    }
  };

  const loadDiamondRanking = async () => {
    try {
      const ranking = await diamondsService.getRanking();
      setDiamondRanking(ranking);
    } catch (error) {
      console.error('Erro ao carregar ranking de diamantes:', error);
    }
  };



  // Extrair primeiro nome do usuário
  const getFirstName = (fullName: string) => {
    return fullName ? fullName.split(' ')[0] : 'Usuário';
  };

  // Gera lista de meses de março de 2024 até o mês atual
  const generateMonthOptions = () => {
    const months = [];
    const startDate = new Date(2024, 2, 1); // Março de 2024 (mês 2 = março)
    const endDate = new Date();

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const monthName = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      
      months.push({
        value: `${year}-${String(month).padStart(2, '0')}`,
        label: monthName.charAt(0).toUpperCase() + monthName.slice(1)
      });

      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return months.reverse(); // Mês mais recente primeiro
  };

  // Filtra o ranking de diamantes por nome e role
  const getFilteredDiamondRanking = () => {
    return diamondRanking.filter(user => {
      const matchesSearch = user.nome_razao_social.toLowerCase().includes(diamondSearchTerm.toLowerCase());
      const matchesRole = diamondSelectedRoles.includes(user.role);
      return matchesSearch && matchesRole;
    });
  };

  // Toggle role filter
  const toggleRoleFilter = (role: string) => {
    setDiamondSelectedRoles(prev => {
      if (prev.includes(role)) {
        return prev.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header de boas-vindas */}
      <Card className="bg-gradient-to-r from-primary to-primary-hover text-primary-foreground" data-tour="welcome">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary-foreground/20 rounded-full flex items-center justify-center">
                <LayoutDashboard className="w-8 h-8 text-gold" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold mb-1">
                  Olá, {user ? getFirstName(user.nome_razao_social) : 'Usuário'}! 👋
                </h1>
                {user && (
                  <p className="text-primary-foreground/60 text-sm mt-1">
                    {user.role === 'admin' ? 'Admin' : 
                             user.role === 'coordenadora' ? 'Coordenador(a)' : 'Revendedor(a)'}
                  </p>
                )}
              </div>
            </div>
            {/* <Button 
              variant="ghost" 
              size="sm"
              className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
              onClick={startWalkthrough}
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Tour
            </Button> */}
          </div>
        </CardContent>
      </Card>

      {/* Cards de estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-tour="stats">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
              </div>
              {stat.title === 'Revendedoras(es)' && (
                <div>
                  <p className='text-sm text-muted-foreground'>{TotalCoord || '--'} Coordenadoras(es)</p>
                </div>
              )}

              {stat.title === 'Vendas Baixadas' && (() => {
                const totalVendas = stats.find(s => s.title === 'Total de Vendas')?.value;
                const totalBaixado = stat.value;
                let percentage = 0;
                if (typeof totalVendas === 'string' && typeof totalBaixado === 'string') {
                  const vendasNumber = parseFloat(totalVendas.replace(/R\$\s*/, '').replace(/\./g, '').replace(',', '.'));
                  const baixadoNumber = parseFloat(totalBaixado.replace(/R\$\s*/, '').replace(/\./g, '').replace(',', '.'));
                  if (vendasNumber > 0) {
                    percentage = (baixadoNumber / vendasNumber) * 100;
                  }
                }
                return (
                  <div>
                    <p className='text-sm text-muted-foreground'>{percentage.toFixed(2)} % das vendas</p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Abas */}
      <Card data-tour="tabs">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ActivityIcon className="w-5 h-5" />
            Dashboard - Home
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="geral" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="geral" className="flex items-center gap-1">
                <ChartArea className="w-4 h-4" />
                <span className="hidden sm:inline">Geral</span>
              </TabsTrigger>
              <TabsTrigger value="vendas" className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Vendas</span>
              </TabsTrigger>
              <TabsTrigger value="dashboard-diamantes" className="flex items-center gap-1">
                <Gem className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Diamantes</span>
              </TabsTrigger>
              {/* <TabsTrigger value="diamantes" className="flex items-center gap-1">
                <Medal className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Ranking</span>
              </TabsTrigger> */}
              <TabsTrigger value="indicacoes" className="flex items-center gap-1">
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Indicações</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="geral" className="mt-6">
              {activityStatus ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart de Revendedoras */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-center">Status de Revendedoras</h3>
                    <div className="flex justify-center">
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Ativas (últimos 30 dias)', value: activityStatus.activeRevendedoras },
                              { name: 'Inativas', value: activityStatus.inactiveRevendedoras }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#10b981" />
                            <Cell fill="#ef4444" />
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-sm text-muted-foreground mt-4 text-center">
                      Total: {activityStatus.totalRevendedoras}
                    </p>
                  </div>

                  {/* Pie Chart de Clientes */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-center">Status de Clientes</h3>
                    <div className="flex justify-center">
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Ativos (últimos 30 dias)', value: activityStatus.activeClientes },
                              { name: 'Inativos', value: activityStatus.inactiveClientes }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#3b82f6" />
                            <Cell fill="#9ca3af" />
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-sm text-muted-foreground mt-4 text-center">
                      Total: {activityStatus.totalClientes}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Carregando dados...</p>
              )}
            </TabsContent>

            <TabsContent value="vendas" className="mt-6">
              <div className="space-y-6">
                {/* Vendas últimos 30 dias */}
                {salesLast30Days && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Vendas - Últimos 30 Dias</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Dados */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Período Atual</p>
                            <p className="text-3xl font-bold">{formatCurrency(salesLast30Days.currentPeriod)}</p>
                             <p className="text-xs text-muted-foreground">
                              {salesLast30Days.currentPeriodStart} até {salesLast30Days.currentPeriodEnd}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Período Anterior (30 dias antes)</p>
                            <p className="text-3xl font-bold">{formatCurrency(salesLast30Days.previousPeriod)}</p>
                             <p className="text-xs text-muted-foreground">
                              {salesLast30Days.previousPeriodStart} até {salesLast30Days.previousPeriodEnd}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Variação</p>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2">
                                {salesLast30Days.status === 'crescimento' && (
                                  <ArrowUp className="w-5 h-5 text-green-500" />
                                )}
                                {salesLast30Days.status === 'queda' && (
                                  <ArrowDown className="w-5 h-5 text-red-500" />
                                )}
                                <p className={`text-3xl font-bold ${
                                  salesLast30Days.status === 'crescimento' ? 'text-green-500' :
                                  salesLast30Days.status === 'queda' ? 'text-red-500' : 'text-gray-500'
                                }`}>
                                  {salesLast30Days.growth >= 0 ? '+' : ''}{formatCurrency(salesLast30Days.growth)}
                                </p>
                              </div>
                                <p className={`text-sm font-semibold -mt-10 ${
                                  salesLast30Days.status === 'crescimento' ? 'text-green-600' :
                                  salesLast30Days.status === 'queda' ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {salesLast30Days.status === 'crescimento' ? '↑' : salesLast30Days.status === 'queda' ? '↓' : '→'} {Math.abs(salesLast30Days.growthPercentage).toFixed(2)}%
                                </p>
                            </div>
                          
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Vendas por Mês */}
                {salesByMonth && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Vendas por Mês</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Seletor de Mês */}
                        <div className="flex gap-4 items-end">
                          <div className="flex-1">
                            <label className="text-sm font-medium mb-2 block">Selecione o Mês</label>
                            <select
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              {generateMonthOptions().map((month) => (
                                <option key={month.value} value={month.value}>
                                  {month.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Dados do Mês Selecionado */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Total de Vendas</p>
                            <p className="text-3xl font-bold">{formatCurrency(salesByMonth.totalSales)}</p>
                            <p className="text-xs text-muted-foreground">
                              Mês: {salesByMonth.monthName}/{salesByMonth.year}
                            </p>
                          </div>

                          <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Quantidade de Vendas</p>
                            <p className="text-3xl font-bold">{salesByMonth.quantity}</p>
                            <p className="text-xs text-muted-foreground">Número de transações</p>
                          </div>

                          <div className="space-y-2 p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Ticket Médio</p>
                            <p className="text-3xl font-bold">{formatCurrency(salesByMonth.averagePerSale)}</p>
                            <p className="text-xs text-muted-foreground">Valor médio por venda</p>
                          </div>

                          <div className="space-y-2 p-4 rounded-lg" 
                            style={{
                              backgroundColor: salesByMonth.totalSales >= 150000 ? '#dcfce7' : '#fee2e2'
                            }}>
                            <p className="text-sm text-muted-foreground">Comparativo com Média Mensal 2025</p>
                            <p className="text-3xl font-bold">
                              {salesByMonth.totalSales >= 150000 ? '+' : '-'}
                              {formatCurrency(Math.abs(salesByMonth.totalSales - 150000))}
                            </p>
                            <div className='flex items-center gap-2 mt-2'>
                              <p className={`text-sm flex items-center font-semibold ${
                                salesByMonth.totalSales >= 150000 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {/* percentages */}
                                {salesByMonth.totalSales >= 150000 && (
                                  <ArrowUp className="w-5 h-5 text-green-500" />
                                )}
                                {salesByMonth.totalSales < 150000 && (
                                  <ArrowDown className="w-5 h-5 text-red-500" />
                                )}
                                {salesByMonth.totalSales >= 150000 ? '+'+(salesByMonth.totalSales * 100 / 150000 - 100).toFixed(2) + '%' : '-' + (100 - salesByMonth.totalSales * 100 / 150000).toFixed(2) + '%'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                • Média: R$ 150.000,00
                              </p>
                            </div>
                            
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}</div>
            </TabsContent>

            <TabsContent value="dashboard-diamantes" className="mt-6">
              <DiamondDashboard />
            </TabsContent>

            <TabsContent value="diamantes" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Ranking - 40% no desktop (2 colunas de 5) */}
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Medal className="w-5 h-5 text-yellow-500" />
                        Top Diamantes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Cabeçalho Expansível */}
                        <button
                          onClick={() => setDiamondFiltersExpanded(!diamondFiltersExpanded)}
                          className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          <span className="text-sm font-medium">Filtros</span>
                          {diamondFiltersExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-600" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          )}
                        </button>

                        {/* Conteúdo Expansível */}
                        {diamondFiltersExpanded && (
                          <div className="space-y-4 pt-2 border-t">
                            {/* Busca por Nome */}
                            <div>
                              <label className="text-sm font-medium mb-2 block">Buscar por Nome</label>
                              <input
                                type="text"
                                placeholder="Digite um nome..."
                                value={diamondSearchTerm}
                                onChange={(e) => setDiamondSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              />
                            </div>

                            {/* Filtro por Role - Horizontal */}
                            <div>
                              <label className="text-sm font-medium mb-2 block">Filtrar por Tipo</label>
                              <div className="flex flex-wrap gap-3">
                                {[
                                  { value: 'revendedora', label: 'Revendedora' },
                                  { value: 'cliente', label: 'Cliente' },
                                  { value: 'admin', label: 'Admin' },
                                  { value: 'coordenadora', label: 'Coordenadora' }
                                ].map(role => (
                                  <div key={role.value} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id={`role-${role.value}`}
                                      checked={diamondSelectedRoles.includes(role.value)}
                                      onChange={() => toggleRoleFilter(role.value)}
                                      className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                                    />
                                    <label
                                      htmlFor={`role-${role.value}`}
                                      className="ml-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                                    >
                                      {role.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Ranking */}
                        <div className="max-h-[400px] overflow-y-auto border-t pt-4">
                          <div className="space-y-3">
                            {getFilteredDiamondRanking().length > 0 ? (
                              getFilteredDiamondRanking().map((user, index) => (
                                <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3 flex-1">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs ${
                                      index === 0 ? 'bg-yellow-500' :
                                      index === 1 ? 'bg-gray-400' :
                                      index === 2 ? 'bg-orange-600' :
                                      'bg-blue-500'
                                    }`}>
                                      {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-sm truncate">{user.nome_razao_social}</p>
                                      <p className="text-xs text-muted-foreground">{user.role === 'coordenadora' ? 'Coordenadora' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 ml-2">
                                    <Gem className="w-4 h-4 text-blue-500" />
                                    <p className="font-bold text-sm">{user.diamantes}</p>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="text-muted-foreground text-center py-8 text-sm">Nenhum resultado encontrado</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Espaço livre - 60% no desktop (3 colunas de 5) */}
                <div className="lg:col-span-3">
                  <Card className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed">
                    <div className="text-center p-8">
                      <Diamond className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-muted-foreground">Espaço reservado para futuros dados</p>
                    </div>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="indicacoes" className="mt-6">
              <IndicacoesDashboard />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

    </div>
  );
};

export default Index;
